import BaseApi from '../../base/base'
import { ApiStatus } from '../../api_constant'

export interface AiChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface AiChatContext {
  body: string
  selectedText: string
  cursorLine: number
  isEmptyLine: boolean
  selectionStartLine: number
  selectionEndLine: number
}

export interface AiChatImage {
  url: string
  filename: string
}

export interface AiChatRequest {
  messages: AiChatMessage[]
  context: AiChatContext
  promptId?: number
  threadId?: number
  model?: string
  systemPrompt?: string
  images?: AiChatImage[]
  thinkingEnabled?: boolean
  mode?: 'agent' | 'ask'
}

export interface AiChatResponse {
  success: boolean
  error?: string
}

/**
 * API for AI Sidebar Chat feature
 * Uses AiProviders with gpt-4o (streamed via ActionCable)
 */
export default class AiChatApi extends BaseApi {
  constructor() {
    super()
  }

  private csrfToken(): string {
    const element = document?.head?.querySelector('[name=csrf-token]') as HTMLMetaElement | null
    return element?.content || ''
  }

  /**
   * Send chat messages with editor context
   * Response is streamed via ActionCable (ChatExamGenerationChannel)
   */
  async send(examId: number, params: AiChatRequest): Promise<AiChatResponse> {
    this.path = `/admin/exams/${examId}/ai_chats`

    const requestBody: any = {
      messages: params.messages,
      context: {
        body: params.context.body,
        selected_text: params.context.selectedText,
        cursor_line: params.context.cursorLine,
        is_empty_line: params.context.isEmptyLine,
        selection_start_line: params.context.selectionStartLine,
        selection_end_line: params.context.selectionEndLine
      }
    }

    if (params.promptId) {
      requestBody.prompt_id = params.promptId
    }

    if (params.threadId) {
      requestBody.thread_id = params.threadId
    }

    if (params.model) {
      requestBody.model = params.model
    }

    if (params.systemPrompt) {
      requestBody.system_prompt = params.systemPrompt
    }

    if (params.images && params.images.length > 0) {
      requestBody.images = params.images
    }

    if (params.thinkingEnabled) {
      requestBody.thinking_enabled = true
    }

    if (params.mode) {
      requestBody.mode = params.mode
    }

    if ((window as any).embedToken) {
      requestBody.embed_token = (window as any).embedToken
    }

    try {
      const response = await fetch(this.url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': this.csrfToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      })

      const text = await response.text()
      let payload: any = {}
      try {
        payload = text ? JSON.parse(text) : {}
      } catch (_error) {
        payload = {}
      }

      const apiStatus = payload?.status
      if (!response.ok || apiStatus !== ApiStatus.SUCCESS) {
        const serverError =
          payload?.message ||
          payload?.data?.error ||
          (response.status === 403 ? 'Monthly AI token limit reached' : undefined)
        return {
          success: false,
          error: serverError || 'Failed to start AI chat'
        }
      }

      return { success: true }
    } catch (error: any) {
      console.error('AI Chat API error:', error)
      return {
        success: false,
        error: error.message || 'Failed to start AI chat'
      }
    }
  }
}
